import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type CreateActualInput,
  type CreatePositionInput,
  type ListPositionsFilters,
  type MonthlyResponse,
  type PositionDetail,
  type PositionDto,
  type SavingsRateTrendResponse,
  type SummaryResponse,
  type UpdatePositionInput,
  cashflowApi,
} from "../lib/cashflow";

const baseKey = ["cashflow"] as const;

export function useCashflowPositions(filters: ListPositionsFilters = {}) {
  return useQuery<{ positions: PositionDto[] }>({
    queryKey: [...baseKey, "list", filters],
    queryFn: () => cashflowApi.listPositions(filters),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useCashflowPosition(id: string) {
  return useQuery<PositionDetail>({
    queryKey: [...baseKey, "detail", id],
    queryFn: () => cashflowApi.getPosition(id),
    staleTime: Number.POSITIVE_INFINITY,
    enabled: Boolean(id),
  });
}

export function useCashflowMonthly(month?: string) {
  return useQuery<MonthlyResponse>({
    queryKey: [...baseKey, "monthly", month ?? "current"],
    queryFn: () => cashflowApi.getMonthly(month),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useSavingsRateTrend(months: number) {
  return useQuery<SavingsRateTrendResponse>({
    queryKey: [...baseKey, "savings-rate-trend", months],
    queryFn: () => cashflowApi.getSavingsRateTrend(months),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useCashflowSummary() {
  return useQuery<SummaryResponse>({
    queryKey: [...baseKey, "summary"],
    queryFn: () => cashflowApi.getSummary(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: baseKey });
}

export function useCreatePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePositionInput) => cashflowApi.createPosition(input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdatePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdatePositionInput }) =>
      cashflowApi.updatePosition(id, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useSoftDeletePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cashflowApi.softDeletePosition(id),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useHardDeletePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cashflowApi.hardDeletePosition(id),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAddActual() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ positionId, input }: { positionId: string; input: CreateActualInput }) =>
      cashflowApi.addActual(positionId, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteActual() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ positionId, actualId }: { positionId: string; actualId: string }) =>
      cashflowApi.deleteActual(positionId, actualId),
    onSuccess: () => invalidateAll(qc),
  });
}
